import React, { useState, useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';

// Single source of truth for what the modal can offer. The complete catalog
// lives here; the parent passes in `availableTariffs` / `availableFindings`
// / `availableCountries` so the modal hides options that aren't present on
// the current run. Keeping the catalog client-side (instead of letting the
// server dictate labels) means the labels stay consistent with the rest of
// the UI even if the server returns the raw prefix keys.
const TARIFF_OPTIONS = [
  { key: 'ch98_hts2', label: 'Chapter 98' },
  { key: 'sec_301', label: 'Section 301' },
  { key: 'sec_301_flip', label: 'Section 301 Flip' },
  { key: 'sec_232_auto', label: 'Sec 232 Auto' },
  { key: 'sec_232_mhdv', label: 'Sec 232 MHDV' },
  { key: 'sec_232_steel', label: 'Sec 232 Steel' },
  { key: 'sec_232_alum', label: 'Sec 232 Aluminum' },
  { key: 'sec_232_copper', label: 'Sec 232 Copper' },
  { key: 'sec_232_consol_metal', label: 'Sec 232 Consol. Metal' },
  { key: 'sec_232_sw_timber', label: 'Sec 232 Softwood Timber' },
  { key: 'sec_232_semiconductor', label: 'Sec 232 Semiconductor' },
  { key: 'sec_232_pharma', label: 'Sec 232 Pharma' },
  { key: 'sec_232_uas', label: 'Sec 232 UAS' },
  { key: 'ieepa_co_specific', label: 'IEEPA Country Specific' },
  { key: 'ieepa_reciprocal', label: 'IEEPA Reciprocal' },
  { key: 'sec_122', label: 'Section 122' },
  { key: 'sec_338', label: 'Section 338' },
  { key: 'sec_201_qsp', label: 'Sec 201 QSP' },
  { key: 'civil_air', label: 'Civil Aircraft' },
  { key: 'other_ch99', label: 'Other Chapter 99' },
];

// Findings the ACE processor can emit. Keep this in step with the flag-type
// strings used in aceAnalysis.processor.ts. Data Error and Processing Error
// belong here (not in TARIFF_OPTIONS — they're flag types, not Ch.99
// prefixes). The trailing three have no live emitter — Tariff Duty
// Discrepancy and Missing Fees are commented out in the processor and Extra
// Fees no longer appears there at all — but are retained so historical runs
// that did emit them remain filterable.
const FINDING_OPTIONS = [
  'Missing Data',
  'BITE Warning',
  'Missing Tariff Quantity',
  'Missing Ch. 99',
  'Unexpected Ch. 99',
  'Base Duty Discrepancy',
  'Tariff Ordering',
  'Needs Tariff Tool Review',
  'Metal Content Exceeds Line',
  'Ineligible FTA',
  'Potential AD/CVD',
  'Data Error',
  'Processing Error',
  'Tariff Duty Discrepancy',
  'Missing Fees',
  'Extra Fees',
];

interface UnifiedFilterModalProps {
  handleClose: () => void;
  activeTariffs: string[];
  activeFindings: string[];
  activeCountries?: string[];
  /**
   * Options actually present on the current run. Filtered against the
   * catalogs above so the modal only shows what's relevant. If any of
   * these are undefined the modal falls back to showing the full catalog
   * (i.e. legacy callers behave the same as before).
   */
  availableTariffs?: string[];
  availableFindings?: string[];
  availableCountries?: string[];
  onApplyFilters: (
    tariffs: string[],
    findings: string[],
    countries: string[],
  ) => void;
  isLoading: boolean;
}

const UnifiedFilterModal: React.FC<UnifiedFilterModalProps> = ({
  handleClose,
  activeTariffs,
  activeFindings,
  activeCountries = [],
  availableTariffs,
  availableFindings,
  availableCountries,
  onApplyFilters,
  isLoading,
}) => {
  const [localTariffs, setLocalTariffs] = useState<string[]>([]);
  const [localFindings, setLocalFindings] = useState<string[]>([]);
  const [localCountries, setLocalCountries] = useState<string[]>([]);

  useEffect(() => {
    setLocalTariffs(activeTariffs);
    setLocalFindings(activeFindings);
    setLocalCountries(activeCountries);
  }, [activeTariffs, activeFindings, activeCountries]);

  const toggleTariff = (key: string) => {
    setLocalTariffs((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const toggleFinding = (finding: string) => {
    setLocalFindings((prev) =>
      prev.includes(finding)
        ? prev.filter((f) => f !== finding)
        : [...prev, finding],
    );
  };

  const toggleCountry = (iso: string) => {
    setLocalCountries((prev) =>
      prev.includes(iso) ? prev.filter((c) => c !== iso) : [...prev, iso],
    );
  };

  const handleClearAll = () => {
    setLocalTariffs([]);
    setLocalFindings([]);
    setLocalCountries([]);
  };

  // Filter the catalogs by what's actually on this run. When the caller
  // doesn't pass an availability list (legacy usage) we show the full
  // catalog so old behavior is preserved. Sort countries alphabetically
  // for the column display.
  const visibleTariffs = availableTariffs
    ? TARIFF_OPTIONS.filter((opt) => availableTariffs.includes(opt.key))
    : TARIFF_OPTIONS;
  // Findings travel as their raw type string end-to-end (the server compares
  // f->>'type' directly), so the catalog above only supplies display order
  // and the retired entries. Anything the run actually carries that the
  // catalog doesn't know about is appended rather than dropped — intersecting
  // alone meant a flag type added to the processor became silently
  // unfilterable until this file caught up.
  const visibleFindings = availableFindings
    ? FINDING_OPTIONS.filter((f) => availableFindings.includes(f)).concat(
        availableFindings
          .filter((f) => f && FINDING_OPTIONS.indexOf(f) === -1)
          .sort(),
      )
    : FINDING_OPTIONS;
  const visibleCountries = (availableCountries || [])
    .filter(Boolean)
    .slice()
    .sort();

  return (
    <div
      className='audit-add-modal-cont'
      style={{ maxWidth: '800px', width: '100%' }}
    >
      {/* Header */}
      <div className='audit-add-modal-cont-head'>
        <div className='audit-add-modal-head-cont-change'>
          <div className='audit-add-modal-head-cont-icon'>
            <FilterListIcon
              sx={{ color: '#4895EF', width: '25px', height: '24px' }}
            />
          </div>
          <div className='audit-add-modal-head-cont-supporting'>
            <h1>Filter Results</h1>
            <p>
              Narrow down your data by applied tariffs or specific
              discrepancies.
            </p>
          </div>
        </div>
        <div
          className='audit-add-modal-head-close'
          onClick={handleClose}
          style={{ cursor: 'pointer' }}
        >
          <svg
            xmlns='http://www.w3.org/2000/svg'
            width='24'
            height='24'
            viewBox='0 0 24 24'
            fill='none'
          >
            <path
              d='M18 6L6 18M6 6L18 18'
              stroke='#85888E'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      {/* Body - Two Columns */}
      <div
        className='audit-add-modal-cont-body'
        style={{
          paddingTop: '24px',
          paddingBottom: '24px',
          maxHeight: '550px',
          overflowY: 'auto',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '32px',
            width: '100%',
            paddingLeft: '24px',
            paddingRight: '24px',
          }}
        >
          {/* Tariffs */}
          {visibleTariffs.length > 0 && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#667085',
                  textTransform: 'uppercase',
                  marginBottom: '16px',
                  letterSpacing: '0.05em',
                }}
              >
                Tariff Types
              </h3>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
              >
                {visibleTariffs.map((opt) => (
                  <div
                    key={opt.key}
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => toggleTariff(opt.key)}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          localTariffs.includes(opt.key) ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            localTariffs.includes(opt.key) ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p style={{ margin: 0 }}>{opt.label}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Findings */}
          {visibleFindings.length > 0 && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#667085',
                  textTransform: 'uppercase',
                  marginBottom: '16px',
                  letterSpacing: '0.05em',
                }}
              >
                Discrepancies & Flags
              </h3>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
              >
                {visibleFindings.map((finding) => (
                  <div
                    key={finding}
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => toggleFinding(finding)}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          localFindings.includes(finding) ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            localFindings.includes(finding) ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p style={{ margin: 0 }}>{finding}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Countries — sorted alphabetically. Long lists scroll inside
              this column rather than expanding the modal — the modal body
              is already scrollable but a tall country list could push the
              other columns off-screen on shorter viewports. */}
          {visibleCountries.length > 0 && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3
                style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#667085',
                  textTransform: 'uppercase',
                  marginBottom: '16px',
                  letterSpacing: '0.05em',
                }}
              >
                Country of Origin
              </h3>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  paddingRight: '4px',
                }}
              >
                {visibleCountries.map((iso) => (
                  <div
                    key={iso}
                    className='bill-container-fit-screen-content-row-checkbox'
                    onClick={() => toggleCountry(iso)}
                  >
                    <div className='bill-container-fit-screen-content-row-input'>
                      <div
                        className={`bill-container-fit-screen-content-row-input-icon ${
                          localCountries.includes(iso) ? 'selected' : ''
                        }`}
                      >
                        <div
                          className={`bill-container-fit-screen-content-row-input-icon-rnd ${
                            localCountries.includes(iso) ? 'selected' : ''
                          }`}
                        />
                      </div>
                    </div>
                    <div className='bill-container-fit-screen-content-row-checkbox-text'>
                      <p style={{ margin: 0 }}>{iso}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='activity-cont-frame-left-table-feed-row-cont-divider'></div>

      {/* Footer */}
      <div className='audit-modal-cont-footer'>
        <div
          className='audit-modal-cont-footer-content'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <div
            className='audit-modal-cont-footer-content-btn-cancel'
            onClick={handleClearAll}
            style={{ width: '150px', padding: '0 24px', flex: 'none' }}
          >
            <div className='audit-modal-cont-footer-content-btn-text'>
              Clear All
            </div>
          </div>

          <div
            className='audit-modal-cont-footer-content-btn-upload'
            onClick={() =>
              onApplyFilters(localTariffs, localFindings, localCountries)
            }
            style={{ width: '150px', padding: '0 24px', flex: 'none' }}
          >
            <div
              className='audit-modal-cont-footer-content-btn-text'
              style={{
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {isLoading && (
                <CircularProgress
                  style={{ width: '16px', height: '16px', marginRight: '8px' }}
                  color='inherit'
                />
              )}
              Apply Filters
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedFilterModal;
